import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { AppRoutingModule } from "./routes/app-routing.module";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { TranslationModule } from './shared/translation.module';
import { AppComponent } from './app.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { PipesModule } from './pipes/pipes.module';
import { AppService } from './services/app.service';
import { TranslateModule } from '@ngx-translate/core';
import { CookieModule, CookieService } from 'ngx-cookie';
import { ServiceWorkerModule, } from '@angular/service-worker';
import { environment, amSuitePlatformOptions } from '../environments/environment';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { provideAmSuitePlatform } from '@neworg-net/amsuite-core';
import { providePrimeUiLicense } from '@neworg-net/amsuite-core/config';
import { PARTY_OWNER_SOURCE, IPartyOwnerSource } from '@neworg-net/amsuite-core/auth';
import { UserProperty } from './properties';
import { AuthService } from './services/auth.service';
import { provideAmSuiteTelemetry } from '@neworg-net/amsuite-core/telemetry';
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SpeechRecognitionService } from './services/speech-recognition.service';
import { SignalRConfig, SignalRService, SignalrWindow } from './services/signalr.service';
import { NotificationSessionService } from './services/notifications/notification-session.service';
import { STOP_NOTIFICATION_SESSION } from './services/notifications/notification-session-control';
import { providePrimeNG } from 'primeng/config';
import { AmSuitePreset } from '../assets/primeng/AmSuiteCSS';
import { AmSuiteAssistantBotComponent, markedOptionsFactory } from '@neworg-net/amsuite-assistant-bot';
import { AlertComponent } from './components/alert/alert.component';
import { SupportComponent } from './components/support/support.component';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { AppStateHolder } from './shared/app-state.holder';
import { TimeLine } from './shared/time-line/time-line';
import { Interceptor } from './shared/interceptor';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

const signalRConfig = new SignalRConfig();
signalRConfig.hubName = "DispatcherHub";

@NgModule({
  declarations: [AppComponent],
  exports: [],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [TranslateModule,
  CookieModule.forRoot(),
    A11yModule,
    FormsModule,
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    PipesModule,
    TranslationModule,
    ConfirmDialogModule,
    ToastModule,
    OverlayBadgeModule,
    ButtonModule,
    DrawerModule,
    PopoverModule,
    RippleModule,
    TooltipModule,
    // Il solo componente della chat, MAI AmSuiteAssistantBotModule (#1489): fino alla 2.2.79 il
    // modulo porta un provideHttpClient suo, e alla radice il suo interceptor entrava nella catena
    // di tutte le chiamate dell'app. Il renderer markdown della chat e' fra i providers.
    AmSuiteAssistantBotComponent,
    AlertComponent,
    SupportComponent,
    UserMenuComponent,
  ServiceWorkerModule.register('ngsw-worker.js', {
    enabled: environment.production,
    registrationStrategy: 'registerWhenStable:10000'
  }), SidebarComponent],

  providers: [AppService,
    CookieService,
    ConfirmationService,
    MessageService,
    AppStateHolder,
    TimeLine,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: Interceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (session: NotificationSessionService) => () => session.initialize(),
      deps: [NotificationSessionService],
      multi: true,
    },
    { provide: SignalrWindow, useValue: window },
    { provide: 'channel.config', useValue: signalRConfig },
    {
      provide: STOP_NOTIFICATION_SESSION,
      useFactory: (session: NotificationSessionService) => () => session.stop(),
      deps: [NotificationSessionService],
    },

    SpeechRecognitionService,
    AuthService,
    SignalRService,
    // Il renderer markdown della chat (blocchi mermaid come <div class="mermaid">), che prima
    // arrivava col MarkdownModule.forRoot di AmSuiteAssistantBotModule.
    provideMarkdown({ markedOptions: { provide: MARKED_OPTIONS, useFactory: markedOptionsFactory } }),
    provideAmSuitePlatform(amSuitePlatformOptions),
    // Telemetria Application Insights centralizzata nella lib (instrumentationKey/appId
    // dal ConfigService). AppName 'AmSuite' per la proprieta' di telemetria.
    provideAmSuiteTelemetry({ appName: 'AmSuite' }),
    // Adapter app-specifico per X-Party-Owner-Id consumato dal PartyOwnerResolver
    // della lib. current = stato sincrono (UserProperty.dataOwnerId); fetch = monolite
    // legacy /suite/getdataowner (ESCLUSO dal perimetro, normalizza stringa vs DataOwner.id);
    // set = persiste l'owner risolto.
    // NOTA: il fetch legacy è anche usato da LegacySessionService per rilevare
    // scadenza del cookie ASP (.AspNet.Cookies) mentre il Bearer moderno è ancora valido.
    {
      provide: PARTY_OWNER_SOURCE,
      useFactory: (up: UserProperty, app: AppService): IPartyOwnerSource => ({
        current: () => {
          const v = up.dataOwnerId;
          return typeof v === 'string' && v.trim() ? v.trim() : null;
        },
        fetchDataOwner: async () => {
          const r = await app.getDataOwner();
          if (typeof r === 'string') return r.trim() ? r.trim() : null;
          const id = r?.id;
          return typeof id === 'string' && id.trim() ? id.trim() : null;
        },
        set: (id: string) => { up.dataOwnerId = id; },
      }),
      deps: [UserProperty, AppService],
    },
    // La licenza PrimeUI va registrata PRIMA che providePrimeNG la verifichi:
    // PrimeNG 22 controlla la licenza a runtime all'inizializzazione della config.
    providePrimeUiLicense(),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: AmSuitePreset,
        options: {
          darkModeSelector: 'none'
        }
      }
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi())]
})


export class AppModule {
}
